/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef } from 'react';
import { presignedURLGet } from 'modules/FileManagerV2/api/services';
import { useToast } from 'context/ToastContext';
import { toastConstant } from 'constants/toastmessage';
import constants from 'constants/index';

export function useFileManagerEffects(deps) {
  const {
    location,
    userContext,
    isCaseFileManager,
    isLeadFileManager,
    isClientFileManager,
    casePage,
    notificationCounts,
    readClientNotifications,
    websocketContext,
    files,
    setFileManagerFilters,
    setBreadCrumbs,
    setShowPreview,
    setSelectedItem,
    setSelectedPreviewItem,
    setPreviewList,
    setDeletePermission,
    setSummaryGenerationStatus,
  } = deps;

  const { addToast } = useToast();
  const prevCaseIdRef = useRef(null);
  const prevResTypeIdRef = useRef(null);

  const normalizeStatus = (status) => {
    if (typeof status !== 'string') return status;

    const value = status.toLowerCase();
    if (value === 'in progress' || value === 'in_progress') {
      return 'in_progress';
    }
    return value;
  };

  const buildSummaryFromSingle = (summary) => {
    if (!summary?.file_id) return null;

    const status = normalizeStatus(summary?.summary_generation_status);
    const isError = status === 'failed' || summary?.isError === true;

    return {
      fileId: summary.file_id,
      data: {
        ...summary,
        summary_generation_status: status,
        error_status: isError,
        summary_status: status === 'success' ? 'ready' : isError ? 'failed' : 'processing',
      },
    };
  };

  const buildSummaryFromVersions = (versions) => {
    if (!versions?.length) return null;

    const latest = versions[versions.length - 1];
    const fileId = latest?.parameters?.file_id;

    if (!fileId) return null;

    const isError = latest?.error_status === true;
    const hasS3Path = Boolean(latest?.s3_path?.length);

    const status = isError ? 'failed' : hasS3Path ? 'success' : 'in_progress';

    return {
      fileId,
      data: {
        ...latest,
        file_id: fileId,
        summary_versions: versions,
        summary_generation_status: status,
        error_status: isError,
        summary_status: status === 'success' ? 'ready' : status === 'in_progress' ? 'processing' : 'failed',
      },
    };
  };

  useEffect(() => {
    if (location?.state?.isFromDashboardSignatures) {
      setFileManagerFilters((prevFilters) => ({
        ...prevFilters,
        esignature_status: 'SIGNED',
      }));
    }
  }, [location?.state?.isFromDashboardSignatures, setFileManagerFilters]);

  useEffect(() => {
    const state = location?.state;
    if (!state) return;

    const handlePreview = async () => {
      const fileFromState = state?.file;
      const filePath = fileFromState?.file_path || fileFromState?.object_key;

      if (!filePath) return;

      try {
        const normalizedFile = {
          ...fileFromState,
          file_path: fileFromState?.file_path || fileFromState?.object_key,
          object_id: fileFromState?.object_id || fileFromState?.file_id || fileFromState?.document_id,
        };

        const res = await presignedURLGet(filePath, normalizedFile?.version_id, false, true);

        const fileWithUrl = { ...normalizedFile, url: res?.data?.file_url };

        setShowPreview(true);
        setSelectedItem(fileWithUrl);
        setSelectedPreviewItem(fileWithUrl);
        setPreviewList([fileWithUrl]);
      } catch (error) {
        addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.ERROR, 'File Not Found');
      }
    };

    const handleBreadCrumb = () => {
      if (state?.breadCrumbList?.length > 0) {
        const crumbs = state.breadCrumbList;
        const folderId = crumbs[crumbs.length - 1]?.value;
        setBreadCrumbs(crumbs);
        if (folderId) {
          setFileManagerFilters((prev) => ({
            ...prev,
            selectedFolder: folderId,
            folder_id: folderId,
          }));
        }
      } else if (state?.breadCrumb) {
        const breadCrumb = state.breadCrumb;
        const folderId = breadCrumb?.value;

        // breadCrumbs (plural) carries the full multi-level trail (e.g. from dashboard
        // RecentFiles). Fall back to a single-item array for backward compatibility.
        const breadCrumbs = state?.breadCrumbs;
        if (Array.isArray(breadCrumbs) && breadCrumbs?.length > 0) {
          setBreadCrumbs(breadCrumbs);
        } else {
          setBreadCrumbs([{ label: breadCrumb?.label, value: folderId }]);
        }

        if (folderId) {
          setFileManagerFilters((prev) => ({
            ...prev,
            selectedFolder: folderId,
            folder_id: folderId,
          }));
        }
      } else if (state?.folderId) {
        const folderId = state.folderId;
        setFileManagerFilters((prev) => ({
          ...prev,
          selectedFolder: folderId,
          folder_id: folderId,
        }));
      }
    };

    handlePreview();
    handleBreadCrumb();
  }, [location?.state]);

  useEffect(() => {
    if (!isCaseFileManager && !isLeadFileManager) return;

    const parts = location.pathname.split('/');
    const idx = parts.findIndex((p) => p === 'cases' || p === 'leads');
    const id = parts[idx + 1];

    if (!id) return;

    if (id !== prevCaseIdRef.current && prevCaseIdRef.current !== null && !location.state?.breadCrumb) {
      setBreadCrumbs([]);
      setFileManagerFilters((prev) => ({ ...prev, selectedFolder: '', folder_id: '' }));
    }

    prevCaseIdRef.current = id;
  }, [location.pathname, isCaseFileManager, isLeadFileManager]);

  useEffect(() => {
    const currentResTypeId = location?.state?.res_type_id;
    if (currentResTypeId && !prevResTypeIdRef.current) {
      prevResTypeIdRef.current = currentResTypeId;
    } else if (currentResTypeId && currentResTypeId !== prevResTypeIdRef.current) {
      setBreadCrumbs([]);
      setFileManagerFilters((prev) => ({ ...prev, selectedFolder: '', folder_id: '' }));
      prevResTypeIdRef.current = currentResTypeId;
    }
  }, [location?.state?.res_type_id, setFileManagerFilters]);

  useEffect(() => {
    if (location?.state?.file) return;

    const viewFileData = localStorage.getItem('viewFile');
    if (!viewFileData) return;

    try {
      const parsed = JSON.parse(viewFileData);
      const filePath = parsed?.file_path || parsed?.object_key;
      if (!filePath) return;

      localStorage.removeItem('viewFile');

      const normalizedFile = {
        ...parsed,
        file_path: parsed?.file_path || parsed?.object_key,
        object_id: parsed?.object_id || parsed?.file_id || parsed?.document_id,
      };

      presignedURLGet(filePath, normalizedFile?.version_id, false, true)
        .then((res) => {
          const fileWithUrl = { ...normalizedFile, url: res?.data?.file_url };

          setShowPreview(true);
          setSelectedItem(fileWithUrl);
          setSelectedPreviewItem(fileWithUrl);
          setPreviewList([fileWithUrl]);
        })
        .catch(() => {
          addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.ERROR, 'File Not Found');
        });
    } catch (err) {
      console.error('Invalid viewFile in localStorage');
      localStorage.removeItem('viewFile');
    }
  }, []);

  useEffect(() => {
    if (!userContext?.permissions) return;

    const isCaseWriteAllowed = userContext.getPermission('cases') === constants.permissions.WRITE;
    const isFirmDeleteAllowed = userContext.getFirmPermission(constants.permissions.DELETE_ITEMS) !== false;
    const canDelete = (!casePage || isCaseWriteAllowed) && isFirmDeleteAllowed;

    setDeletePermission(canDelete);
  }, [userContext?.permissions, casePage]);

  useEffect(() => {
    if (userContext?.userDetails?.contact_id && isClientFileManager && notificationCounts?.file_count) {
      readClientNotifications('file', userContext.userDetails.contact_id);
    }
  }, [userContext?.userDetails, notificationCounts, isClientFileManager, readClientNotifications]);

  useEffect(() => {
    if (!files?.length) return;

    setSummaryGenerationStatus((prev) => {
      const updated = { ...prev };
      let hasUpdates = false;

      files.forEach((file) => {
        const fileId = file?.file_id;
        const existing = updated[fileId];
        let normalizedStatus = file?.summary?.summary_generation_status;

        if (!fileId || !file?.summary?.summary_generation_status) return;
        if (typeof normalizedStatus === 'string') {
          normalizedStatus = normalizedStatus.toLowerCase();
          if (normalizedStatus === 'in progress' || normalizedStatus === 'in_progress') normalizedStatus = 'in_progress';
        }

        const apiStatusObj = {
          summary_generation_status: normalizedStatus,
          file_id: fileId,
          version_id: file?.version_id,
          file_path: file?.file_path,
          s3_path: file?.summary?.s3_path,
          error_status: normalizedStatus === 'failed' || file?.summary?.isError === true,
          ...file?.summary,
        };

        if (!existing || existing.summary_generation_status !== normalizedStatus || existing.s3_path !== apiStatusObj.s3_path) {
          updated[fileId] = { ...existing, ...apiStatusObj };
          hasUpdates = true;
        }
      });
      return hasUpdates ? updated : prev;
    });
  }, [files]);

  useEffect(() => {
    const wsData = websocketContext?.aiFileSummary;
    if (!wsData) return;

    const { summary, summary_versions } = wsData.body || {};

    const result = buildSummaryFromSingle(summary) || buildSummaryFromVersions(summary_versions);

    if (!result) return;

    setSummaryGenerationStatus((prev) => ({
      ...prev,
      [result.fileId]: result.data,
    }));
  }, [websocketContext?.aiFileSummary]);
}

export default useFileManagerEffects;
